import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import List from '@/components/List/List';
import Modal, { useModalContext } from '@/components/Modal/Modal';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/ui/radio-group';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { useStructuredStorageFilterQuery } from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import { cn } from '@/lib/cn';
import { popup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import { capitalizeFirstLetterInWord } from '@/utils/util';

import useProductStore from '../../store/ProductStore';
import type { ProductHierarchy } from '../../types';
import { BaseTable } from '../BaseTable';

type Props = {
  items: {
    id: string;
    name: string;
  }[];
  target: ProductHierarchy;
  mergeCallback: (targetId: string, list: string[]) => Promise<void>;
  onDoneCallback?: () => void;
};

export const MergeModal = (props: Props) => {
  const { items, target } = props;
  const { closeModal } = useModalContext();
  const { clearFilter } = useStructuredStorageFilterQuery(
    STORAGE_KEYS.FEEDBACK_FILTER_STATUS_KEY,
  );

  const queryClient = useQueryClient();

  const clearCache = () => {
    clearFilter();
    queryClient.invalidateQueries();
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const message = useMemo(() => {
    switch (target) {
      case 'brand':
        return 'Changing this will unlink all associated feedback from this brand.';
      case 'line':
        return 'Changing this will unlink all associated feedback from this product line.';
      case 'product':
        return 'Changing this will unlink all associated feedback from this product and update the following:';
      case 'option':
        return 'Changing this will unlink all associated feedback from this option and update the following:';
      default:
        throw new Error(`Invalid target: ${target}`);
    }
  }, [target]);

  const onDoneClick = async () => {
    if (!selectedId) return;
    popup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Are you sure you want to merge?',
      style: { width: 700 },
      message: (
        <div className="flex flex-col gap-4 w-full">
          <span>{message}</span>
          <InfoTable target={target} items={items} selectedId={selectedId} />
        </div>
      ),
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: async () => {
        await props.mergeCallback(
          selectedId,
          items.map((item) => item.id),
        );
        toast(
          `${capitalizeFirstLetterInWord(target)} merge complete`,
          'success',
          3000,
          {
            body: `Your ${target}s have been successfully merged.`,
          },
        );
        await closeModal();
        clearCache();
        props.onDoneCallback?.();
      },
    });
  };

  return (
    <Modal.Layout className="max-h-[calc(100vh-64px)]" modalSize="m">
      <Modal.Header>
        <Modal.Title>Merge {target}s</Modal.Title>
      </Modal.Header>
      <Modal.Body className="flex-1 overflow-auto flex flex-col gap-6">
        <span>
          Select the {target} to keep as the primary.
          <br />
          All unselected {target}s will be deleted, and their associated
          feedback will be moved to the selected {target}.
        </span>
        <RadioGroup>
          <List
            className="gap-2"
            data={items}
            render={(item) => {
              const selected = item.id === selectedId;
              return (
                <div
                  key={item.id}
                  className={cn(
                    'flex items-center gap-2 justify-between border px-4 py-3.5 rounded-md transition-all duration-100 cursor-pointer',
                    selected && 'bg-muted border-ring',
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <RadioGroupItem value={item.id} checked={selected} />
                    <div>{item.name}</div>
                  </div>
                  {selected && (
                    <div className="text-xs text-muted-foreground">
                      Merge into this
                    </div>
                  )}
                </div>
              );
            }}
          />
        </RadioGroup>
      </Modal.Body>
      <Modal.Footer
        cancel={{
          variant: 'secondary',
          children: 'Cancel',
          onClick: closeModal,
        }}
        confirm={{
          variant: 'primary',
          children: 'Merge',
          disabled: !selectedId,
          onClick: onDoneClick,
        }}
      ></Modal.Footer>
    </Modal.Layout>
  );
};

const InfoTable = (props: {
  target: ProductHierarchy;
  items: {
    id: string;
    name: string;
  }[];
  selectedId: string;
}) => {
  const { target, items, selectedId } = props;
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const groupedProductMap = useProductStore((state) => state.groupedProductMap);
  const productOptionMap = useProductStore((state) => state.productOptionMap);

  const table = useMemo(() => {
    switch (target) {
      case 'brand':
        return (
          <BaseTable
            columns={[
              {
                columnKey: 'brand',
                Header: 'Brand',
                Cell: ({ rowData }) => {
                  const targetBrandName = brandMap.get(selectedId)?.name;
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <div className="flex items-center gap-1 flex-wrap">
                        <TooltipV2 tooltipNode={rowData.name}>
                          <span className="line-clamp-1">{rowData.name}</span>
                        </TooltipV2>
                      </div>
                      <ArrowRight className="size-3.5" />
                      <TooltipV2 tooltipNode={targetBrandName}>
                        <span className="line-clamp-1">{targetBrandName}</span>
                      </TooltipV2>
                    </div>
                  );
                },
                className: 'flex-1',
              },
            ]}
            data={items
              .filter((item) => item.id !== selectedId)
              .map((item) => ({
                rowKey: item.id,
                rowData: item,
              }))}
          />
        );
      case 'line':
        return (
          <BaseTable
            columns={[
              {
                columnKey: 'line',
                Header: 'Line',
                Cell: ({ rowData }) => {
                  const targetLineName = lineMap.get(selectedId)?.name;
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <TooltipV2 tooltipNode={rowData.name}>
                        <span className="line-clamp-1">{rowData.name}</span>
                      </TooltipV2>
                      <ArrowRight className="size-3.5" />
                      <TooltipV2 tooltipNode={targetLineName}>
                        <span className="line-clamp-1">{targetLineName}</span>
                      </TooltipV2>
                    </div>
                  );
                },
                className: 'flex-1',
              },
            ]}
            data={items
              .filter((item) => item.id !== selectedId)
              .map((item) => ({
                rowKey: item.id,
                rowData: item,
              }))}
          />
        );
      case 'product': {
        const targetBrand = groupedProductMap.get(selectedId)?.brand_id;
        const targetLine = groupedProductMap.get(selectedId)?.line_id;
        const targetProductName =
          groupedProductMap.get(selectedId)?.product_name;
        const targetBrandName = targetBrand
          ? brandMap.get(targetBrand)?.name
          : null;
        const targetLineName = targetLine
          ? lineMap.get(targetLine)?.name
          : null;

        return (
          <BaseTable
            columns={[
              {
                columnKey: 'product',
                Header: 'Product',
                Cell: ({ rowData }) => {
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <TooltipV2 tooltipNode={rowData.name}>
                        <span className="line-clamp-1">{rowData.name}</span>
                      </TooltipV2>
                      <ArrowRight className="size-3.5" />
                      <TooltipV2 tooltipNode={targetProductName}>
                        <span className="line-clamp-1">
                          {targetProductName}
                        </span>
                      </TooltipV2>
                    </div>
                  );
                },
                className: 'flex-1',
              },
              {
                columnKey: 'brand',
                Header: 'Brand',
                Cell: ({ rowData }) => {
                  const currentOption = productOptionMap.get(rowData.id);
                  const currentProduct = currentOption?.product_alias
                    ? groupedProductMap.get(currentOption?.product_alias)
                    : null;
                  const currentBrand = currentProduct?.brand_id;
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <TooltipV2
                        tooltipNode={
                          currentBrand && brandMap.get(currentBrand)?.name
                        }
                      >
                        <span className="line-clamp-1">
                          {currentBrand
                            ? brandMap.get(currentBrand)?.name
                            : '-'}
                        </span>
                      </TooltipV2>
                      <ArrowRight className="size-3.5" />
                      <TooltipV2 tooltipNode={targetBrandName}>
                        <span className="line-clamp-1">{targetBrandName}</span>
                      </TooltipV2>
                    </div>
                  );
                },
                className: 'w-40',
              },
              {
                columnKey: 'line',
                Header: 'Line',
                Cell: ({ rowData }) => {
                  const currentOption = productOptionMap.get(rowData.id);
                  const currentProduct = currentOption?.product_alias
                    ? groupedProductMap.get(currentOption?.product_alias)
                    : null;
                  const currentLine = currentProduct?.line_id;
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <TooltipV2
                        tooltipNode={
                          currentLine && lineMap.get(currentLine)?.name
                        }
                      >
                        <span className="line-clamp-1">
                          {currentLine ? lineMap.get(currentLine)?.name : '-'}
                        </span>
                      </TooltipV2>
                      <ArrowRight className="size-3.5" />
                      <TooltipV2 tooltipNode={targetLineName}>
                        <span className="line-clamp-1">{targetLineName}</span>
                      </TooltipV2>
                    </div>
                  );
                },
                className: 'w-40',
              },
            ]}
            data={items
              .filter((item) => item.id !== selectedId)
              .map((item) => ({
                rowKey: item.id,
                rowData: item,
              }))}
          />
        );
      }

      case 'option': {
        const targetOption = productOptionMap.get(selectedId);
        const targetProduct = targetOption
          ? groupedProductMap.get(targetOption.product_alias)
          : null;
        const targetProductName = targetProduct?.product_name;

        return (
          <BaseTable
            columns={[
              {
                columnKey: 'option',
                Header: 'Option',
                Cell: ({ rowData }) => {
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <TooltipV2 tooltipNode={rowData.name}>
                        <span className="line-clamp-1">{rowData.name}</span>
                      </TooltipV2>
                      <ArrowRight className="size-3.5" />
                      <TooltipV2 tooltipNode={targetOption?.option_name}>
                        <span className="line-clamp-1">
                          {targetOption?.option_name}
                        </span>
                      </TooltipV2>
                    </div>
                  );
                },
                className: 'flex-1',
              },
              {
                columnKey: 'product',
                Header: 'Product',
                Cell: ({ rowData }) => {
                  const currentOption = productOptionMap.get(rowData.id);
                  const currentProduct = currentOption?.product_alias
                    ? groupedProductMap.get(currentOption?.product_alias)
                    : null;
                  const currentProductName = currentProduct?.product_name;
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <TooltipV2 tooltipNode={currentProductName || '-'}>
                        <span className="line-clamp-1">
                          {currentProductName || '-'}
                        </span>
                      </TooltipV2>
                      <ArrowRight className="size-3.5" />
                      <TooltipV2 tooltipNode={targetProductName}>
                        <span className="line-clamp-1">
                          {targetProductName}
                        </span>
                      </TooltipV2>
                    </div>
                  );
                },
                className: 'w-80',
              },
            ]}
            data={items
              .filter((item) => item.id !== selectedId)
              .map((item) => ({
                rowKey: item.id,
                rowData: item,
              }))}
          />
        );
      }

      default:
        throw new Error(`Invalid target: ${target}`);
    }
  }, [
    target,
    items,
    selectedId,
    brandMap,
    lineMap,
    groupedProductMap,
    productOptionMap,
  ]);

  return table;
};
